import React from 'react';
import {FormProvider} from 'react-hook-form';
import {defineMessages} from 'react-intl';
import {defineAccessControlledResource, GroupAccessRule} from '@igp/shared/access-control';
import type {EditMode} from '@igp/shared/components/button';
import {Button} from '@igp/shared/components/button';
import {useEditButton} from '@igp/shared/components/editable';
import {LocalizedText} from '@igp/shared/components/i18n';
import {HeaderCell} from '@igp/shared/components/table';
import type {AgentRevenueShareViewModel} from '@igp/shared/features/user-profile';
import {useAgentRevenueShareUpdate, useUserProfiles} from '@igp/shared/features/user-profile';
import {Table, TableCell, TableRow} from '@mui/material';
import {makeStyles} from 'tss-react/mui';

import {groups, useBoAgentAccessCheck} from '@access-control';
import {PlayerReferral} from '@features/shared-blocks/PlayerReferral';

import {RevenueShareManagementTable} from './RevenueShareManagementTable';

const localized = defineMessages({
    agentId: {
        id: 'RevenueShareManagementModal_agentId',
        defaultMessage: 'Agent ID',
    },
    username: {
        id: 'RevenueShareManagementModal_username',
        defaultMessage: 'Username',
    },
    edit: {
        id: 'RevenueShareManagementModal_edit',
        defaultMessage: 'Edit',
    },
    save: {
        id: 'RevenueShareManagementModal_save',
        defaultMessage: 'Save',
    },
    cancel: {
        id: 'RevenueShareManagementModal_cancel',
        defaultMessage: 'Cancel',
    },
});

const useStyles = makeStyles()(theme => ({
    form: {
        margin: theme.spacing(2, 2, 3, 2),
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
    },
    header: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
    },
    buttonContainer: {
        display: 'flex',
        flexDirection: 'row',
        gap: theme.spacing(2),
    },
    metaInfo: {
        width: 'fit-content',
    },
}));

type RevenueShareManagementModalProps = {
    agentId: string;
    agentUsername: string;
};
export function RevenueShareManagementModal({agentId, agentUsername}: RevenueShareManagementModalProps) {
    const {classes} = useStyles();
    const {items} = useUserProfiles({
        ids: [agentId],
        viewType: 'RevenueShareDetails',
        fields: [
            'uid',
            'agent_info.latest_revenue_share.poker_cash.nlh_sd',
            'agent_info.latest_revenue_share.poker_mtt',
            'agent_info.latest_revenue_share.poker_cash.plo',
            'agent_info.latest_revenue_share.casino_share',
            'agent_info.latest_revenue_share.spins',
            'agent_info.latest_revenue_share.sports',
            'commission_subtypes',
            'master_agent_id',
        ],
        validateFilter: () => Boolean(agentId),
        currency: 'USD',
    });
    const revenueShare: AgentRevenueShareViewModel = items[0]?.latest_revenue_share;
    const isMasterAgent = agentId && items[0]?.master_agent_id === agentId;
    const formMethods = useAgentRevenueShareUpdate({agentId, initialModel: revenueShare, isMasterAgent});
    const {handleSubmit, submit, reset, formState, state} = formMethods;

    const mode: EditMode = state.isProgress ? 'InProgress' : state.isError ? 'Edit' : 'Read';
    const {currentMode, handleSave, handleCancel, handleEdit} = useEditButton({
        mode,
        isMultiStep: false,
        onSave: () => {},
        onCancel: reset,
    });

    function onSubmit(value: AgentRevenueShareViewModel) {
        submit(value);
        handleSave();
    }
    const isEditAvailable = useBoAgentAccessCheck(agentManagementRevenueShareEditResource);
    const isSaveDisabled = state.isProgress || !formState.isDirty;
    return (
        <form onSubmit={handleSubmit(onSubmit)} className={classes.form}>
            <div className={classes.header}>
                <Table className={classes.metaInfo}>
                    <TableRow>
                        <HeaderCell>
                            <LocalizedText label={localized.agentId} />
                        </HeaderCell>
                        <HeaderCell>
                            <LocalizedText label={localized.username} />
                        </HeaderCell>
                    </TableRow>
                    <TableRow>
                        <TableCell>{agentId}</TableCell>
                        <TableCell>{agentUsername}</TableCell>
                    </TableRow>
                </Table>
                <div className={classes.buttonContainer}>
                    {currentMode === 'Read' ? (
                        <Button type="button" onClick={handleEdit} disabled={!isEditAvailable}>
                            <LocalizedText label={localized.edit} />
                        </Button>
                    ) : null}
                    {currentMode !== 'Read' ? (
                        <>
                            <Button type="submit" disabled={isSaveDisabled}>
                                <LocalizedText label={localized.save} />
                            </Button>

                            <Button type="reset" onClick={handleCancel} disabled={state.isProgress}>
                                <LocalizedText label={localized.cancel} />
                            </Button>
                        </>
                    ) : null}
                </div>
            </div>
            <FormProvider {...formMethods}>
                <RevenueShareManagementTable mode={currentMode} isMasterAgent={isMasterAgent} />
            </FormProvider>
            <PlayerReferral uid={agentId} />
        </form>
    );
}

export const agentManagementRevenueShareEditResource = defineAccessControlledResource({
    name: 'AgentManagementRevenueShareEditResource',
    rules: new GroupAccessRule([groups.agentManagersGroup, groups.adminsGroup]),
});
